import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import BreadCrumbs from './components/atoms/BreadCrumbs';
import DebugPanel from './components/molecules/DebugPanel';
import { showNotification } from './components/molecules/Notifications';
import AuthStep from './components/organisms/AuthStep';
import Basket from './components/organisms/Basket';
import PaymentStep from './components/organisms/PaymentStep';
import ShippingStep from './components/organisms/ShippingStep';
import { setActiveAbTests } from './utils/abTests';
import { useCheckoutState } from './utils/checkoutState';
import { config } from './utils/config';
import getConfigNotifications from './utils/getConfigNotifications';
import { getInitialRoute, getStepFromRoute } from './utils/routing';
import { pushInitialTrackingEvents, pushTrackingEvent } from './utils/tracking';
import useIsMobileDevice from './utils/useIsMobileDevice';
import useStickyScrolling from './utils/useStickyScrolling';

const App = () => {
  const { contentRef, contentStyles, basketStyles } = useStickyScrolling();
  const { checkoutState } = useCheckoutState();
  const { formatMessage } = useIntl();

  const isMobile = useIsMobileDevice();
  const location = useLocation();
  const urlParams = new URLSearchParams(window.location.search);
  const enableRuleEngineDebug = urlParams.get('enableRuleEngineDebug');
  const previousPathnameRef = useRef<string>();

  const step = getStepFromRoute(location.pathname);

  const formContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveAbTests(config.abTests, checkoutState.customer);
    setTimeout(() => {
      if (!formContainerRef?.current?.childNodes?.length) {
        window.location.reload();
      }
    }, 250);
    try {
      pushInitialTrackingEvents(checkoutState, step);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (location.pathname === '/' || location.pathname === previousPathnameRef.current) {
      return;
    }

    previousPathnameRef.current = location.pathname;

    window.scrollTo({ top: 0, behavior: 'smooth' });

    pushTrackingEvent({
      event: 'content_view',
      content_name: `${window.location.pathname}/${step}`,
      search: window.location.search || null,
      app_version: 'v3',
      appId: config.appId,
      page_type: 'checkout:' + step
    });

    (async () => {
      const configNotifications = await getConfigNotifications(step);
      configNotifications.forEach(notification => {
        showNotification({
          level: notification.type ?? 'info',
          text: formatMessage({ id: notification.textTranslationKey }),
          autoClose: true
        });
      });
    })();
  }, [location]);

  const isBreadcrumbsEnabled = config.features.breadcrumbs.enabled;
  const isAuthStep = getStepFromRoute(location.pathname) === 'auth';
  const isBasketTop = config.basket.position === 'top';
  const isPaymentStepWithBasketTop = isBasketTop && getStepFromRoute(location.pathname) === 'payment';
  const showBasket = !isMobile || (isAuthStep && !config.basket.hideOnAuthStep) || isPaymentStepWithBasketTop;
  const showDebugPanel = !isMobile && checkoutState.debug && !!(Number(enableRuleEngineDebug) === 1);

  return (
    <>
      {showDebugPanel && <DebugPanel />}
      {isBreadcrumbsEnabled && (
        <div className="show-mobile">
          <BreadCrumbs />
        </div>
      )}
      <div className="main">
        <div className="content-container" ref={contentRef} style={contentStyles}>
          <div className="content-container__max-width">
            {isBreadcrumbsEnabled && (
              <div className="show-desktop">
                <BreadCrumbs />
              </div>
            )}
            <div className="form-container" ref={formContainerRef}>
              <Routes>
                <Route path="/" element={<Navigate to={getInitialRoute(checkoutState)} replace />} />
                <Route path="/auth" element={<AuthStep />} />
                <Route path="/shipping" element={<ShippingStep />} />
                <Route path="/payment" element={<PaymentStep />} />
              </Routes>
            </div>
          </div>
        </div>
        {showBasket && <Basket desktopStickyStyle={basketStyles} />}
      </div>
    </>
  );
};

export default App;
